import { useState, useEffect } from 'react';
import { X, Copy, AlertTriangle, Loader2 } from 'lucide-react';
import { api } from '../api/client';
import type { SessionInfo, SessionTag } from '../api/types';
import { useInstanceMode } from '../hooks/useInstanceMode';
import './ResetModal.css';
import './CloneDialog.css';

interface Props {
  sessionName: string;
  source: SessionInfo | null;
  /** Source session's history-tags — offered as branch points once the backend
   *  supports clone-at-tag (TAG_CLONE_SUPPORTED). */
  tags: SessionTag[];
  onClose: () => void;
  onCloned?: (newName: string) => void;
}

// Clone-at-tag (branch the transcript at a tag into the NEW session) — LIVE on
// Cloud's /clone since 2026-06-23 (commit 4ffd217): `from_tag` branches at the
// tagged turn, implies clone_history. Server resolves tag→turn_uuid.
const TAG_CLONE_SUPPORTED = true;

type Phase = 'idle' | 'running' | 'error';

export function CloneDialog({ sessionName, source, tags, onClose, onCloned }: Props) {
  const [newName, setNewName] = useState(`${sessionName}-clone`);
  const [newOwner, setNewOwner] = useState('');
  const [cloneHistory, setCloneHistory] = useState(false);
  const [ephemeral, setEphemeral] = useState(false);
  const [ttlHours, setTtlHours] = useState(24);
  const [start, setStart] = useState(true);
  const [fromTag, setFromTag] = useState('');
  const [node, setNode] = useState(''); // '' = same node as source
  const [nodes, setNodes] = useState<Array<{ name: string; is_self: boolean }>>([]);
  const [localNode, setLocalNode] = useState('mac');
  const instanceMode = useInstanceMode();
  const [phase, setPhase] = useState<Phase>('idle');
  const [errorText, setErrorText] = useState('');

  // Federation nodes for the "run on" picker (clone-then-relocate in one call).
  useEffect(() => {
    api.cluster.nodes()
      .then((r) => { setLocalNode(r.local || 'mac'); setNodes(r.nodes.map((n) => ({ name: n.name, is_self: n.is_self }))); })
      .catch(() => {});
  }, []);
  const sourceNode = source?.node && source.node !== 'local' ? source.node : localNode;

  const nameValid = /^[A-Za-z0-9._-]+$/.test(newName.trim());

  const submit = async () => {
    if (!nameValid || phase === 'running') return;
    setPhase('running');
    setErrorText('');
    try {
      const r = await api.sessions.clone(sessionName, {
        new_name: newName.trim(),
        new_owner: newOwner.trim() || undefined,
        // from_tag implies clone_history server-side; OR an explicit toggle.
        clone_history: cloneHistory || !!(TAG_CLONE_SUPPORTED && fromTag),
        ephemeral,
        ttl_hours: ephemeral ? ttlHours : undefined,
        start,
        from_tag: TAG_CLONE_SUPPORTED && fromTag ? fromTag : undefined,
        node: node && node !== sourceNode ? node : undefined,
      });
      if (r?.error) { setPhase('error'); setErrorText(r.error); return; }
      onCloned?.(r?.name || newName.trim());
      onClose();
    } catch (e) {
      setPhase('error');
      setErrorText(e instanceof Error ? e.message : 'Klonen fehlgeschlagen');
    }
  };

  return (
    <div className="reset-modal-backdrop" onClick={onClose}>
      <div className="reset-modal clone-modal" onClick={(e) => e.stopPropagation()}>
        <header className="reset-modal-header">
          <h2><Copy size={18} /> Agent klonen — {sessionName}</h2>
          <button className="reset-modal-close" onClick={onClose}><X size={16} /></button>
        </header>

        <div className="reset-modal-warning">
          <AlertTriangle size={14} />
          <span>
            Kopiert die komplette Agent-Konfiguration (Persona, MCP, Model) unter
            einem neuen Namen. Mit „History mitkopieren" setzt der Klon die
            Conversation der Quelle fort, sonst startet er frisch.
          </span>
        </div>

        <label className="reset-modal-label clone-inline">
          Name des Klons
          <input
            className="reset-modal-input"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="z.B. CloudV2-test"
            autoFocus
          />
        </label>
        {!nameValid && newName.length > 0 && (
          <div className="clone-hint clone-hint-warn">Nur Buchstaben, Zahlen, . _ - erlaubt.</div>
        )}

        <label className="reset-modal-label clone-inline">
          Owner (optional)
          <input
            className="reset-modal-input"
            value={newOwner}
            onChange={(e) => setNewOwner(e.target.value)}
            placeholder={source?.owner_email || 'Quelle übernehmen'}
          />
        </label>

        <label className="clone-check">
          <input type="checkbox" checked={cloneHistory} onChange={(e) => setCloneHistory(e.target.checked)} />
          <span>History mitkopieren <small>— Klon setzt die Conversation der Quelle fort (sonst frisch)</small></span>
        </label>

        {/* Clone-at-tag — branch the transcript at a bookmark into the clone.
            Only meaningful when copying history. */}
        {TAG_CLONE_SUPPORTED && cloneHistory && tags.length > 0 && (
          <label className="reset-modal-label clone-inline">
            Ab Lesezeichen (optional)
            <select className="reset-modal-input" value={fromTag} onChange={(e) => setFromTag(e.target.value)}>
              <option value="">Ganze History</option>
              {tags.map((t) => <option key={t.tag} value={t.tag}>{t.tag}</option>)}
            </select>
          </label>
        )}

        {/* Run-on node — clone then relocate to the target in one call.
            Standalone: ein Knoten, kein Ziel — das Feld wäre eine Frage ohne
            zweite Antwort (#4701). Klonen selbst bleibt. */}
        {instanceMode === 'federation' && (
          <label className="reset-modal-label clone-inline">
            Läuft auf
            <select className="reset-modal-input" value={node} onChange={(e) => setNode(e.target.value)}>
              <option value="">wie Quelle ({sourceNode})</option>
              {nodes.filter((n) => n.name !== sourceNode).map((n) => (
                <option key={n.name} value={n.name}>{n.name === 'local' ? 'mac' : n.name}{n.is_self ? ' (hier)' : ''}</option>
              ))}
            </select>
          </label>
        )}

        <label className="clone-check">
          <input type="checkbox" checked={ephemeral} onChange={(e) => setEphemeral(e.target.checked)} />
          <span>Wegwerf-Klon <small>— automatisches Kill nach Ablauf</small></span>
        </label>
        {ephemeral && (
          <label className="reset-modal-label clone-inline">
            Lebensdauer (Stunden)
            <input
              type="number" min={1} max={720} step={1}
              className="reset-modal-input"
              value={ttlHours}
              onChange={(e) => setTtlHours(Math.max(1, Math.min(720, parseInt(e.target.value) || 24)))}
            />
          </label>
        )}

        <label className="clone-check">
          <input type="checkbox" checked={start} onChange={(e) => setStart(e.target.checked)} />
          <span>Sofort starten <small>— Klon-tmux direkt hochfahren</small></span>
        </label>

        {phase === 'error' && <div className="clone-hint clone-hint-warn">{errorText}</div>}

        <div className="reset-modal-actions">
          <button className="reset-modal-cancel" onClick={onClose}>Abbrechen</button>
          <button className="reset-modal-submit" onClick={submit} disabled={!nameValid || phase === 'running'}>
            {phase === 'running' ? <><Loader2 size={14} className="clone-spin" /> Klone…</> : 'Klonen'}
          </button>
        </div>
      </div>
    </div>
  );
}
